import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import CmsForm from "../cmsForm";
import { cms } from "@/constants/translation/cms.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import CmsValidationClass from "@/helpers/validations/cms";
import { useGetCms, useUpdateCms } from "@/hooks/useCms";
import { masters, dashboard } from "@/libs/routes";
import constants from "@/libs/constant";

const ENGLISH_CODE = "en";

/**
 * Component for editing a CMS page (also serves first-time content entry
 * for a page that has no content yet).
 * @returns {JSX.Element} The rendered EditCms component.
 */
const EditCms = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useTranslationContext();
  const queryClient = useQueryClient();

  const { data: response, isLoading: isDataLoading } = useGetCms(id, undefined, () => {
    toast.error(t(messages.toast.cms.dataFetchFailed));
  });

  const { mutate: doUpdateCms, isPending: isSubmitting } = useUpdateCms(
    (res) => {
      toast.success(res?.message || t(messages.toast.cms.updated));
      // Refresh list (Last Updated) and this page's cached copy
      queryClient.invalidateQueries({ queryKey: ["cms-list"] });
      queryClient.invalidateQueries({ queryKey: ["cms", id] });
      navigate(masters.cms.list);
    },
    (err) => {
      toast.error(err?.response?.data?.message || t(messages.toast.cms.updateFailed));
    }
  );

  const cmsData = response?.data;

  // English first, then the rest alphabetically by name
  const translations = useMemo(() => {
    const list = [...(cmsData?.translations || [])];
    list.sort((a, b) => {
      if (a.language_code === ENGLISH_CODE) return -1;
      if (b.language_code === ENGLISH_CODE) return 1;
      return (a.language_name || "").localeCompare(b.language_name || "");
    });
    return list;
  }, [cmsData]);

  const englishLanguageId = translations.find((l) => l.language_code === ENGLISH_CODE)?.language_id;

  const initialValues = useMemo(
    () => ({
      slug: cmsData?.slug || "",
      // API booleans arrive as "true"/"false" strings after the encryption round-trip
      is_active: String(cmsData?.is_active) === "true" ? "true" : "false",
      translations: translations.map((l) => ({
        language_id: l.language_id,
        language_code: l.language_code,
        language_name: l.language_name,
        title: l.title || "",
        description: l.description || "",
        seo_meta_title: l.seo_meta_title || "",
        seo_meta_desc: l.seo_meta_desc || "",
      })),
    }),
    [translations, cmsData]
  );

  const validationSchema = useMemo(
    () => CmsValidationClass.cmsValidation(t, englishLanguageId),
    [t, englishLanguageId]
  );

  const handleUpdate = (values) => {
    // English is always sent; untouched (fully empty) optional languages are omitted
    const payload = values.translations
      .filter(
        (l) =>
          String(l.language_id) === String(englishLanguageId) ||
          l.title.trim() ||
          l.description.trim()
      )
      .map((l) => ({
        language_id: l.language_id,
        title: l.title.trim(),
        description: l.description,
        seo_meta_title: l.seo_meta_title.trim(),
        seo_meta_desc: l.seo_meta_desc.trim(),
      }));
    doUpdateCms({ uuid: id, is_active: values.is_active === "true", translations: payload });
  };

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>{t(cms.edit.loading)}</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  if (!cmsData || translations.length === 0) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p className="text-danger">{t(cms.edit.not_found)}</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{cmsData.title || t(cms.edit.main_heading)}</h1>
        <CmsForm
          initialValues={initialValues}
          onSubmit={handleUpdate}
          isSubmitting={isSubmitting}
          isEdit
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

EditCms.breadCrumbArray = [
  { name: breadcrumb_key.home, pathname: dashboard, active: false },
  {
    name: breadcrumb_key.masters?.cms?.list,
    pathname: masters.cms.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.cms?.edit,
    pathname: "",
    active: true,
  },
];

export default EditCms;
